import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowRight, 
  CheckCircle, 
  Star,
  Users,
  Globe,
  Shield,
  Zap,
  Target
} from 'lucide-react'

const Solutions: React.FC = () => {
  const navigate = useNavigate()
  
  const solutions = [
    {
      id: 'financial',
      icon: Shield,
      title: '金融行业解决方案',
      description: '为银行、保险、证券等金融机构提供Web3技术集成方案',
      features: [
        '数字资产托管',
        '跨境支付',
        '供应链金融',
        '风险管控'
      ],
      color: 'from-blue-500 to-cyan-500',
      industries: ['银行', '保险', '证券', '支付']
    },
    {
      id: 'enterprise',
      icon: Globe,
      title: '企业级解决方案',
      description: '为企业提供Web3技术集成，提升业务效率和安全性',
      features: [
        '身份认证',
        '数据安全',
        '供应链管理',
        '智能合约'
      ],
      color: 'from-purple-500 to-pink-500',
      industries: ['制造业', '零售业', '物流业', '服务业']
    },
    {
      id: 'government',
      icon: Users,
      title: '政府机构解决方案',
      description: '为政府部门提供Web3技术应用，提升公共服务水平',
      features: [
        '数字身份',
        '电子政务',
        '数据治理',
        '公共服务'
      ],
      color: 'from-green-500 to-emerald-500',
      industries: ['政府部门', '公共事业', '教育机构', '医疗机构']
    },
    {
      icon: Zap,
      title: '创新应用解决方案',
      description: '为创新型企业提供Web3技术支持和定制开发服务',
      features: [
        'DeFi协议',
        'NFT平台',
        'GameFi项目',
        'DAO治理'
      ],
      color: 'from-orange-500 to-red-500',
      industries: ['游戏', '娱乐', '内容创作', '社交']
    }
  ]

  const services = [
    {
      title: '技术咨询',
      description: '提供Web3技术咨询和架构设计服务',
      icon: Target
    },
    {
      title: '定制开发',
      description: '根据客户需求提供定制化开发服务',
      icon: Zap
    },
    {
      title: '系统集成',
      description: '将Web3技术集成到现有系统中',
      icon: Shield
    },
    {
      title: '培训支持',
      description: '提供技术培训和持续支持服务',
      icon: Users
    }
  ]

  const caseStudies = [
    {
      title: '某大型银行数字资产托管系统',
      description: '为某大型银行构建了完整的数字资产托管系统，支持多种加密货币的安全存储和管理。',
      results: ['安全性提升99.9%', '运营成本降低30%', '处理效率提升50%'],
      industry: '金融'
    },
    {
      title: '某制造企业供应链管理系统',
      description: '利用区块链技术构建了透明的供应链管理系统，实现了全程可追溯。',
      results: ['透明度提升100%', '欺诈风险降低80%', '协作效率提升40%'],
      industry: '制造'
    },
    {
      title: '某政府部门数字身份系统',
      description: '为某政府部门开发了基于区块链的数字身份系统，提升了公共服务效率。',
      results: ['服务效率提升60%', '身份验证时间缩短70%', '用户满意度提升45%'],
      industry: '政府'
    }
  ]

  return (
    <>
      <Helmet>
        <title>解决方案 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited提供专业的Web3解决方案，包括金融、企业、政府等行业的定制化服务。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                解决方案
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                为不同行业提供专业的Web3技术解决方案
              </p>
            </motion.div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                行业解决方案
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们为不同行业提供定制化的Web3技术解决方案
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {solutions.map((solution, index) => {
                const Icon = solution.icon
                return (
                  <motion.div
                    key={solution.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${solution.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {solution.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {solution.description}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        核心功能：
                      </h4>
                      <ul className="space-y-2">
                        {solution.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        适用行业：
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {solution.industries.map((industry, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                          >
                            {industry}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link
                      to={`/solutions/${solution.id}`}
                      className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors duration-200"
                    >
                      了解更多
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                服务内容
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们提供全方位的Web3技术服务
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                成功案例
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                看看我们如何帮助客户实现数字化转型
              </p>
            </motion.div>

            <div className="space-y-8">
              {caseStudies.map((caseStudy, index) => (
                <motion.div
                  key={caseStudy.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <div>
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {caseStudy.title}
                        </h3>
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-semibold">
                          {caseStudy.industry}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">
                        {caseStudy.description}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      项目成果：
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center text-green-600 dark:text-green-400">
                          <Star className="w-4 h-4 mr-2" />
                          <span className="font-semibold">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                开始您的Web3之旅
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                联系我们，获取专业的Web3解决方案咨询
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-white text-indigo-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <span>联系我们</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300">
                  下载方案
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Solutions